import React, { useMemo, useRef } from 'react';
import { useAuthContext } from '@repo/hooks';
import {
  Section,
  SettingsSectionBox,
  SettingsSectionLabel,
  SettingsButton,
} from '@views/Settings/components';
import styles from './CancelPlan.module.scss';
import { useWorkspaceContext } from '@repo/hooks';
import { getPlanType } from '../ManagePlan';
import { UserSession } from '@repo/utils/types';
import { supportHref } from '@repo/utils/helpers/preferred-email-client';

const handleChurnkeyFailure = () => {
  window.app.confirm(
    {
      title: 'Cancel Plan',
      message: 'Please contact our support team to cancel your plan.',
      action: 'Contact Support',
    },
    (contactSupport) => {
      if (contactSupport) {
        window.open(supportHref({ subject: 'Cancel Plan' }));
      }
    },
  );
};

export const openChurnkeyModal = ({
  user,
  subscriptionId,
  switchingToPersonalPlan = false,
}: {
  user: UserSession;
  subscriptionId?: string;
  switchingToPersonalPlan?: boolean;
}) => {
  const observer = new MutationObserver((mu) => {
    mu.forEach(() => {
      const modal = document.querySelector('.ck-modal');
      if (modal) {
        const error = modal.querySelector('.ck-error-step'); //modal.querySelector('.ck-error-step');

        if (error) {
          window.churnkey.hide();
          window.churnkey.clearState();
          handleChurnkeyFailure();
          observer.disconnect();
        } else {
          if (modal.querySelector('.ck-step')) {
            observer.disconnect();
          }
        }
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });

  window.churnkey.init('show', {
    customerId: user?.stripe_customer_id,
    authHash: user?.churnkey,
    appId: 'cm3qlq54x',
    subscriptionId,
    mode: 'live', // set to 'test' to hit test billing provider environment
    provider: 'stripe',
    record: true, // set to false to skip session playback recording
    customerAttributes: { switchingToPersonalPlan },
  });
};

const CancelPlanButton = () => {
  const { user, getMainSubscriptionID } = useAuthContext();
  const subscriptionId = useMemo(getMainSubscriptionID, []);
  const startCancelPlanFlow = () => openChurnkeyModal({ user, subscriptionId });

  return (
    <SettingsButton onClick={startCancelPlanFlow} className={styles.btn} isInsideBox>
      Cancel Plan
    </SettingsButton>
  );
};

export default function CancelPlan() {
  const workspaceCtx = useWorkspaceContext();
  const authCtx = useAuthContext();
  const planType = useRef(getPlanType(authCtx));
  const userIsAdmin = useRef(workspaceCtx.activeWorkspace && !workspaceCtx.userIsAdmin);
  const user = authCtx.user;
  const featuresStr = 'all paid features';

  if (planType.current === 'free' || !userIsAdmin) {
    return null;
  }

  if (!user || user.billing_provider !== 'Stripe' || !user.churnkey || !user.stripe_customer_id) {
    return null;
  }

  return (
    <Section>
      <SettingsSectionLabel>Cancel Plan</SettingsSectionLabel>
      <SettingsSectionBox className={styles.box}>
        Cancel my plan
        <CancelPlanButton />
        <p>
          When canceling, you'll lose access to{' '}
          {window.desktopIntegrations?.appInfo.isMAS ? (
            <>{featuresStr}</>
          ) : (
            <a href="https://clay.earth/pricing" target="_blank">
              {featuresStr}
            </a>
          )}{' '}
          at the end of your billing period.{' '}
          <a href="https://go.clay.earth/cancellation" target="_blank">
            Learn more
          </a>
          .
        </p>
      </SettingsSectionBox>
    </Section>
  );
}
